import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { cn } from '../cn';

export type TextInputVariant = 'default' | 'mono' | 'underline' | 'inline-rename';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: TextInputVariant;
  block?: boolean;
  /** 28 px compact (colour hex field). */
  compact?: boolean;
}

/**
 * default: 32 px (mobile 34), radius 7/8, bg2, border, 400 12px.
 * mono: same in Geist Mono (passwords, hex, timecode).
 * underline: login fields — no box, 1 px border2 underline, 34 px, 400 15px.
 * inline-rename: 26 px (mobile 34), accent border, 500 13px.
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { variant = 'default', block, compact, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(
        'min-w-0 text-fg outline-none placeholder:text-fg5 disabled:opacity-50',
        (variant === 'default' || variant === 'mono') &&
          cn(
            'border border-border bg-bg2 px-2.5 font-normal',
            compact
              ? 'h-7 rounded-md text-sm'
              : 'h-[34px] rounded-lg text-md md:h-8 md:rounded-md md:text-base',
          ),
        variant === 'mono' && 'font-mono',
        variant === 'underline' &&
          'h-[34px] w-full rounded-none border-0 border-b border-border2 bg-transparent p-0 text-xl font-normal',
        variant === 'inline-rename' &&
          'h-[34px] rounded-lg border border-accent bg-bg2 px-2.5 text-lg font-medium md:h-[26px] md:rounded-[5px] md:px-2 md:text-md',
        block && 'w-full',
        className,
      )}
      {...rest}
    />
  );
});

/** Micro field label + control stack (8 px gap). */
export function Field({
  label,
  children,
  className,
  right,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      <div className="flex justify-between">
        <span className="micro text-fg4">{label}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

/** Mono footnote under a control group: 400 10px/1.5 fg4. */
export function Note({
  children,
  className,
  tone,
}: {
  children: React.ReactNode;
  className?: string;
  tone?: 'ok' | 'warn' | 'accent';
}) {
  return (
    <span
      className={cn(
        'font-mono text-xs font-normal leading-[1.5]',
        tone === 'ok'
          ? 'text-ok tracking-[.04em] font-medium'
          : tone === 'warn'
            ? 'text-warn'
            : tone === 'accent'
              ? 'text-accent'
              : 'text-fg4',
        className,
      )}
    >
      {children}
    </span>
  );
}

/** A secret (password / PIN) shown as dots, with an eye button that reveals the actual value. */
export const SecretInput = forwardRef<HTMLInputElement, Omit<TextInputProps, 'type'>>(function SecretInput(
  { className, block, ...rest },
  ref,
) {
  const [shown, setShown] = useState(false);
  return (
    <div className={cn('relative', block ? 'w-full' : 'inline-block')}>
      <TextInput
        ref={ref}
        block={block}
        type={shown ? 'text' : 'password'}
        className={cn('pr-9', className)}
        {...rest}
      />
      <button
        type="button"
        aria-label={shown ? 'Hide' : 'Show'}
        aria-pressed={shown}
        onClick={() => setShown((v) => !v)}
        className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-fg4 hover:text-fg"
      >
        {shown ? (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.8 21.8 0 0 1 5.06-6.06M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.8 21.8 0 0 1-3.17 4.19M1 1l22 22" />
            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
          </svg>
        ) : (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
});
